import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfoHint } from './info-hint.component';
import './salary-information.component.scss';

interface SalaryInformationProps {
    year: string;
    salaryType: string;
    amount: string;
    onYearChange: (value: string) => void;
    onSalaryTypeChange: (value: string) => void;
    onAmountChange: (value: string) => void;
    errors?: { year?: string; amount?: string };
}

export const SalaryInformation: React.FC<SalaryInformationProps> = ({
    year,
    salaryType,
    amount,
    onYearChange,
    onSalaryTypeChange,
    onAmountChange,
    errors
}) => {
    const { t } = useTranslation();

    const handleAmountChange = (value: string) => {
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            onAmountChange(value);
        }
    };

    // Generate year options (current year to 10 years back)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - i);

    return (
        <div className="salary-information">
            <div className="salary-information__header">
                <div className="ec-shead">
                    <span className="ec-sico" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="20" height="14" x="2" y="5" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" />
                        </svg>
                    </span>
                    <div className="ec-stext">
                        <h3 className="salary-information__title">
                            {t('Information about the salary')}
                        </h3>
                        <p className="ec-sdesc">{t('Provide salary details for the certificate', { defaultValue: 'Provide salary details for the certificate' })}</p>
                    </div>
                </div>
            </div>
            
            <div className="salary-information__body">
                <div className="salary-information__form">
                    <div className="salary-information__field">
                        <label className="salary-information__label">
                            {t('Year')}
                        </label>
                        <div className="salary-information__input-wrapper">
                            <select
                                className={`salary-information__select${errors?.year ? ' salary-information__select--error' : ''}`}
                                value={year}
                                onChange={(e) => onYearChange(e.target.value)}
                            >
                                <option value="">{t('Select year')}</option>
                                {yearOptions.map((yearOption) => (
                                    <option key={yearOption} value={yearOption}>
                                        {yearOption}
                                    </option>
                                ))}
                            </select>
                            <svg className="salary-information__chevron" width="12" height="8" viewBox="0 0 12 8" fill="none">
                                <path d="M1 1.5L6 6.5L11 1.5" stroke="#3C3C43" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        {errors?.year && <span className="salary-information__error-msg">{t(errors.year)}</span>}
                    </div>

                    <div className="salary-information__field">
                        <label className="salary-information__label">
                            {t('Salary type')}
                            <InfoHint text={t('salaryTypeTooltip')} />
                        </label>
                        <div className="salary-information__static-value">
                            {t('Hourly')}
                        </div>
                    </div>

                    <div className="salary-information__field">
                        <label className="salary-information__label">
                            {t('Amount')}
                            <InfoHint text={t('amountTooltip')} />
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            pattern="^\\d*\\.?\\d*$"
                            className={`salary-information__input${errors?.amount ? ' salary-information__input--error' : ''}`}
                            value={amount}
                            onChange={(e) => handleAmountChange(e.target.value)}
                            placeholder={t('Enter amount')}
                        />
                        {errors?.amount && <span className="salary-information__error-msg">{t(errors.amount)}</span>}
                    </div>
                </div>
            </div>
        </div>
    );
};
